<template>
  <div>

    <transition name="f-fade" mode="out-in">
      <div v-if="currentView === ACCOUNT_AUTH_VIEWS.LOGIN">
        <u-alert
          v-if="isEmailTaken"
          class="mb-6"
          color="heroblue"
          title="Welcome back!"
          description="Please sign in to your account."
          icon="i-ri-user-line"
        />
        <u-button
          size="xl"
          class="mb-6"
          block
          :variant="primaryActionVariant"
          :label="loginCtaText"
          @click="goToSignIn"
        />
      </div>

      <div v-else-if="currentView === ACCOUNT_AUTH_VIEWS.SIGNUP">

        <u-form :state="signupState" :schema="signupSchema" @submit="signupUser" :validate-on="['submit']">
          <u-form-group label="Name" name="name" required class="mb-6">
            <u-input v-model="signupState.name" autocomplete="name" />
          </u-form-group>
          <u-form-group label="Email" name="email" required class="mb-6">
            <u-input v-model="signupState.email" autocomplete="email" />
          </u-form-group>
          <u-form-group label="Password" name="password" required class="mb-6">
            <core-password-input v-model="signupState.password" />
          </u-form-group>

          <u-form-group
            v-show="signupState.name && signupState.email && signupState.password"
            name="recaptchaToken"
            class="mb-6"
          >
            <core-recaptcha @verify="onRecaptchaVerify" />
          </u-form-group>

          <u-alert
            v-if="signupError"
            class="my-6"
            color="red"
            title="Sign Up Error"
            :description="signupError"
            icon="i-ri-error-warning-line"
          />
          <u-button
            type="submit"
            :loading="isBusy"
            size="xl"
            class="mb-6"
            block
            :variant="primaryActionVariant"
            :label="signupCtaText"
          />
        </u-form>

      </div>

    </transition>


    <p class="text-center">
      <u-button
        variant="link"
        color="charcoal"
        size="xl"
        :padded="false"
        @click="actionButton.click"
        class="underline-on-hover font-normal"
        :label="actionButton.label"
      />
    </p>
  </div>
</template>

<script setup>
  import {object, string} from 'yup';
  const route = useRoute();
  const router = useRouter();

  const props = defineProps({
    defaultView: {
      type: String,
      default: ACCOUNT_AUTH_VIEWS.SIGNUP
    },
    refreshAfterAuth: Boolean,
    referralToken: String,
    referralSource: String,
    signupCtaText: {
      type: String,
      default: 'Sign Up'
    },
    loginCtaText: {
      type: String,
      default: 'Sign In'
    },
    primaryActionVariant: {
      type: String,
      default: 'solid'
    }
  });

  const emit = defineEmits(['signup-success', 'sign-in']);

  const currentView = ref(props.defaultView);
  const {signUp} = useSessionStore();

  const isBusy = ref(false);

  const actionButton = computed(() => {
    if (currentView.value === ACCOUNT_AUTH_VIEWS.LOGIN) {
      return {
        label: `Don't have an account? Sign up now!`,
        click: () => (currentView.value = ACCOUNT_AUTH_VIEWS.SIGNUP)
      };
    }

    return {
      label: 'I already have an account',
      click: () => (currentView.value = ACCOUNT_AUTH_VIEWS.LOGIN)
    };
  })

  function afterAuthNavigation() {
    if (props.refreshAfterAuth) {
      router.replace({ path: route.fullPath, query: {
        ...route.query,
          refresh: Date.now()
      }});
    }
  }

  function goToSignIn() {
    emit('sign-in');
    navigateTo(useAuthHost().buildSignInUrl(window.location.href), {external: true});
  }

  const signupSchema = object({
    name: string().required('Name is required.'),
    email: string().required('Email is required.').emailWithSuffix('Email is invalid.'),
    password: string()
      .required('Password is required.')
      .min(SESSION_PASSWORD_MIN_LENGTH, `Password must be at least ${SESSION_PASSWORD_MIN_LENGTH} characters`)
      .test(
        'password-complexity',
        'Password must contain an uppercase letter, a lowercase letter, a number, and a special character.',
        value => !value || (/[A-Z]/.test(value) && /[a-z]/.test(value) && /[0-9]/.test(value) && /[^A-Za-z0-9]/.test(value))
      ),
    recaptchaToken: string().required('Please verify you are not a robot.')
  });

  const signupState = reactive({
    name: null,
    email: null,
    password: null,
    referralSource: props.referralSource,
    referralToken: props.referralToken, //todo: name this appropriately
    recaptchaToken: null
  });

  function onRecaptchaVerify(token) {
    signupState.recaptchaToken = token;
  }

  const signupError = ref(null)
  const isEmailTaken = ref(false);
  async function signupUser() {
    isBusy.value = true;
    signupError.value = null;
    isEmailTaken.value = false;

    try {
      const user = await signUp(signupState);
      emit('signup-success', user);
      afterAuthNavigation(user);
    } catch (e) {
      const emailTakenError = e.response?._data?.errors?.email?.[0]?.error === 'taken';
      if (e.response?.status === 400 && emailTakenError || e.response?.status === 409) { //todo: verify this after integration with store
        isEmailTaken.value = true;
        currentView.value = ACCOUNT_AUTH_VIEWS.LOGIN;
      } else {
        signupError.value = 'Something went wrong. Please try again later.'
      }
    } finally {
      isBusy.value = false;
    }
  }
</script>
